/**
 * @genesis_domain Auth Engine
 * @genesis_version V9.1
 * @genesis_intent Recovery OTP view model — manages 8-digit OTP code state, verification handler, and error tracking for the password reset OTP flow
 * @genesis_boundaries No UI rendering, no routing logic
 * @genesis_ai_trail V9.0: Created during tactical DDD refactor to isolate OTP verification logic from Login.tsx.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/context/AuthContext';
import { verifyRecoveryOtp } from '@/domains/auth/services/authService';

export function useRecoveryViewModel() {
    const navigate = useNavigate();
    const { setRecoveryFlow } = useAuth();
    const [otpCode, setOtpCode] = useState('');
    const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
    const [otpError, setOtpError] = useState<string | null>(null);

    const handleVerifyOtp = async (email: string) => {
        if (!otpCode || otpCode.length !== 8) {
            setOtpError('Please enter the complete 8-digit code.');
            return;
        }

        setIsVerifyingOtp(true);
        setOtpError(null);

        try {
            await verifyRecoveryOtp(email, otpCode);
            // On success, Supabase establishes a temporary session.
            // Toggle recovery flow flag so auth guards permit /auth/update-password.
            setRecoveryFlow(true);
            // Navigate to the recovery password update UI.
            navigate('/auth/update-password', { replace: true });
        } catch (err) {
            setOtpError(
                err instanceof Error
                    ? err.message
                    : 'Invalid or expired code. Please request a new reset link.',
            );
        } finally {
            setIsVerifyingOtp(false);
        }
    };

    const resetOtp = () => {
        setOtpCode('');
        setOtpError(null);
        setIsVerifyingOtp(false);
    };

    return {
        otpCode,
        setOtpCode,
        isVerifyingOtp,
        otpError,
        handleVerifyOtp,
        resetOtp,
    };
}
